import { apiService } from '@api/index';
import { TCartItem } from '@apptypes/index';
import { FORCE_SET_CART_ITEMS } from '@store/cart.store';
import { TAppState, TAppEvents } from '@store/index';
import { useEffect, useRef, useState } from 'react';
import { useStoreon } from 'storeon/react';
import { useLinkPath } from './useLinkPath.hooks';
import { useDebounde } from './useDebounce';
import { AxiosError } from 'axios';
import { useLatest } from './useLatesl';
import { useNavigate } from 'react-router-dom';

export const useCart = () => {
  const { dispatch, cartItems } = useStoreon<TAppState, TAppEvents>(
    'cartItems',
  );
  const { isPublicLink, linkPath } = useLinkPath();
  const debouncedProducts = useDebounde(cartItems, 1000);
  const statusRef = useRef<'uninited' | 'actual' | 'readyToSave'>('uninited');
  const latestIsPublicLink = useLatest(isPublicLink);
  const latestLinkPath = useLatest(linkPath);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();

  const loadCart = () => {
    apiService
      .getCart(latestIsPublicLink.current, latestLinkPath.current)
      .then(({ products }) => {
        dispatch(FORCE_SET_CART_ITEMS, products);
      })
      .catch((err: AxiosError) => {
        const status = err.response?.status;

        if (status === 404) {
          dispatch(FORCE_SET_CART_ITEMS, []);
        } else {
          setIsError(true);
        }
      })
      .finally(() => {
        statusRef.current = 'actual';
      });
  };

  const onRepeat = () => {
    statusRef.current = 'uninited';
    setIsError(false);
    loadCart();
  };

  const onCreateNew = () => {
    setIsError(false);
    apiService.deleteCart(isPublicLink, linkPath).finally(() => {
      dispatch(FORCE_SET_CART_ITEMS, null);
      navigate('catalog');
    });
  };

  useEffect(() => {
    loadCart();
  }, []);

  useEffect(() => {
    if (!debouncedProducts) {
      return;
    }

    if (statusRef.current === 'actual') {
      statusRef.current = 'readyToSave';

      return;
    }

    const preparedItems: TCartItem[] = debouncedProducts.map((item) => ({
      id: item.id,
      article: item.article,
      name: item.name,
      quantity: item.quantity,
      code: item.code,
    }));

    apiService.saveCart(
      preparedItems,
      latestIsPublicLink.current,
      latestLinkPath.current,
    );
  }, [debouncedProducts]);

  useEffect(() => {
    if (cartItems === null) {
      dispatch(FORCE_SET_CART_ITEMS, []);
      statusRef.current = 'actual';
    }
  }, [cartItems]);

  const loaded = statusRef.current !== 'uninited';

  return { loaded, isError, onCreateNew, onRepeat };
};
